import { useEffect, useMemo, useState } from 'react';
import { Column, ColumnInstance, TableInstance, useTable, useSortBy } from 'react-table';
import './WellControlConfirmTable.scss';
import { ReactComponent as TrashIcon } from '../../../images/trash.svg';
import { ReactComponent as CheckCircleIcon } from '../../../images/check-circle.svg';
import Badge from '../../../components/common/Badge/Badge';
import { ReactComponent as SignalIcon } from '../../../images/signal-01.svg';
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks';
import { selectProgressDisplay, selectScanStats, setApiCompletedWells, setTimedOutWells } from './WellControlSlice';
import { ALLY_CONFIGURATIONS } from '../../../services/configuration/config';
import Tooltip from '../../common/tooltip/ToolTip';
import { WELL_CONTROL_ACTION_LABELS } from '../MultiwellControlActions/wellControlActionLabels';
import NavigationIcons from '../../navigation/components/NavigationIcons';

export interface WellControlConfirmData {
  assetId: string;
  wellName: string;
  lastGoodScan: string;
  commsStatus: string;
  command: string;
  status: string;
  hasError?: boolean;
  errorMessage?: string;
}

interface WellControlConfirmTableProps {
  data: WellControlConfirmData[];
  name: string;
  onRemoveAsset?: (assetId: string) => void;
  isSuccess?: boolean;
  isProcessing?: boolean;
  showBulkDeleteNotification?: boolean;
  onBulkRemoveFailedAssets?: () => void;
  showScanningProgressNotification?: boolean;
  totalCount?: number;
  scanningProgress?: number;
  apiCompletedWells: Record<string, boolean | null>;
  isWebSocketProcessing?: boolean;
}

const WellControlConfirmTable: React.FC<WellControlConfirmTableProps> = ({
  data,
  name,
  onRemoveAsset,
  isSuccess = false,
  isProcessing = false,
  showBulkDeleteNotification = false,
  onBulkRemoveFailedAssets,
  showScanningProgressNotification = false,
  apiCompletedWells,
  isWebSocketProcessing = false,
}) => {
  const dispatch = useAppDispatch();
  const webSocketMessage = useAppSelector((state) => state.wellControl.nodeIdStatusMap);
  const timedOutWells = useAppSelector((state) => state.wellControl.timedOutWells);
  const scanStats = useAppSelector(selectScanStats);
  const { failedCount, successCount, totalCount } = scanStats;
  const progressDisplay = useAppSelector(selectProgressDisplay);
  const assets = useAppSelector((state) => state.assetGroups?.assets);

  const PROCESSING_TIMEOUT_MS = ALLY_CONFIGURATIONS.WELL_SCAN_TIMEOUT_MS;
  const actionLabels = WELL_CONTROL_ACTION_LABELS[name] || WELL_CONTROL_ACTION_LABELS.Default;

  const nodeIdStatusMap = useMemo(() => webSocketMessage || {}, [webSocketMessage]);

  const [processingStartTimes, setProcessingStartTimes] = useState<Record<string, number>>({});

  useEffect(() => {
    if (isWebSocketProcessing && data.length > 0) {
      setProcessingStartTimes({});
      dispatch(setTimedOutWells({}));
      const now = Date.now();
      const newTimes: Record<string, number> = {};
      data.forEach((well) => {
        newTimes[well.assetId] = now;
      });
      setProcessingStartTimes(newTimes);
    }
  }, [isWebSocketProcessing, data, dispatch]);

  useEffect(() => {
    if (!isWebSocketProcessing) return;
    const interval = setInterval(() => {
      const now = Date.now();
      const newTimedOut: Record<string, boolean> = {};
      Object.entries(processingStartTimes).forEach(([assetId, start]) => {
        if (apiCompletedWells[assetId] === null && now - start > PROCESSING_TIMEOUT_MS) {
          newTimedOut[assetId] = true;
        }
      });
      if (Object.keys(newTimedOut).length > 0) {
        dispatch(setTimedOutWells(newTimedOut));
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isWebSocketProcessing, processingStartTimes, apiCompletedWells, dispatch]);

  useEffect(() => {
    if (isWebSocketProcessing && data.length > 0) {
      dispatch(setApiCompletedWells(Object.fromEntries(data.map((well) => [well.assetId, null]))));
    }
  }, [isWebSocketProcessing, data, dispatch]);

  const updatedData = useMemo(() => {
    let mapped = data.map((item) => {
      const assetId = item.assetId;
      const normalizedWellName = item.wellName.trim().toLowerCase();
      const matchedStatus = nodeIdStatusMap[normalizedWellName];
      const apiStatus = apiCompletedWells[assetId];
      const timedOut = timedOutWells[assetId];

      if (timedOut) {
        return {
          ...item,
          status: 'Failed',
          hasError: true,
          errorMessage: 'Processing timed out',
        };
      }
      if (matchedStatus) {
        return {
          ...item,
          status: matchedStatus,
          hasError: matchedStatus === 'Failed',
          errorMessage: matchedStatus === 'Failed' ? 'Asset has an error' : '',
        };
      }
      if ((apiStatus === null || apiStatus === undefined) && !matchedStatus && !timedOut) {
        return {
          ...item,
          status: 'Processing',
          hasError: false,
          errorMessage: '',
        };
      }
      return {
        ...item,
        status: '',
        hasError: false,
        errorMessage: '',
      };
    });

    mapped = mapped.slice().sort((a, b) => {
      if (a.hasError === b.hasError) return 0;
      return a.hasError ? -1 : 1;
    });

    return mapped;
  }, [data, nodeIdStatusMap, apiCompletedWells, timedOutWells, showBulkDeleteNotification]);

  const columns: Column<WellControlConfirmData>[] = useMemo(() => {
    const getIndustryApplicationId = (wellName: string) => {
      if (!assets) return 0;
      const asset = assets.find((a: any) => a.assetName === wellName);
      return asset?.industryApplicationId ?? 0;
    };

    const baseColumns: Column<WellControlConfirmData>[] = [
      {
        Header: 'Well name',
        accessor: 'wellName' as const,
        sortType: 'alphanumeric',
        Cell: ({ value }: { value: string }) => {
          const industryApplicationId = getIndustryApplicationId(value);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <NavigationIcons asset={{ industryApplicationId }} isActive={false} />
              <span className='data-text well-name-text' title={value}>
                {value}
              </span>
            </div>
          );
        },
      } as any,
      {
        Header: 'Last good scan',
        accessor: 'lastGoodScan' as const,
        sortType: (rowA: any, rowB: any, columnId: string) => {
          const a = rowA.values[columnId] || '';
          const b = rowB.values[columnId] || '';

          // Handle empty values
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;

          // Parse dates for comparison
          const dateA = new Date(a);
          const dateB = new Date(b);

          if (dateA.getTime() === dateB.getTime()) return 0;
          return dateA > dateB ? 1 : -1;
        },
        Cell: ({ value }: { value: string }) => <span className='data-text'>{value || '-'}</span>,
      } as any,
      {
        Header: 'Comms status',
        accessor: 'commsStatus' as const,
        sortType: 'alphanumeric',
        Cell: ({ value }: { value: string }) => <span className='data-text'>{value || '-'}</span>,
      } as any,
      {
        Header: 'Command',
        accessor: 'command' as const,
        sortType: 'alphanumeric',
        Cell: ({ value }: { value: string }) => <span className='data-text'>{value}</span>,
      } as any,
      {
        Header: 'Status',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: { row: { original: WellControlConfirmData; index: number } }) => {
          const originalRow = row.original;
          const dataRow = data.find((d) => d.assetId === originalRow.assetId);
          const errorState = showBulkDeleteNotification ? dataRow?.hasError : originalRow.hasError;
          const errorMessage = showBulkDeleteNotification ? dataRow?.errorMessage : originalRow.errorMessage;
          const status = originalRow.status;

          if (showBulkDeleteNotification && failedWellsCount > 0) {
            if (errorState && onRemoveAsset) {
              const tooltipDirection = row.index === 0 ? 'bottom' : undefined;
              return (
                <div className='actions-cell'>
                  <div className='error-icon' title={errorMessage}>
                    <Tooltip content={errorMessage || 'Asset has an error'} direction={tooltipDirection}>
                      <div className='error-circle'>!</div>
                    </Tooltip>
                  </div>
                  <button
                    className='remove-button'
                    onClick={() => onRemoveAsset(originalRow.assetId)}
                    title='Remove this asset from selection'
                  >
                    <TrashIcon className='remove-icon' />
                  </button>
                </div>
              );
            }
            return <span className='data-text'>-</span>;
          }

          if (!showBulkDeleteNotification && showScanningProgressNotification) {
            if (status === 'Processing') {
              return (
                <Badge icon='processing' size='md'>
                  Processing
                </Badge>
              );
            }
            if (status === 'Success') {
              return (
                <Badge icon='complete' size='md'>
                  Complete
                </Badge>
              );
            }
            if (status === 'Failed') {
              return (
                <Badge icon='error' size='md'>
                  {errorMessage === 'Processing timed out' ? 'Failed, timed out' : 'Failed, comm error code'}
                </Badge>
              );
            }
            if (status) {
              return <Badge size='md'>{status}</Badge>;
            }
            return <span className='data-text'>-</span>;
          }

          return <span className='data-text'>-</span>;
        },
      } as any,
    ];

    return baseColumns;
  }, [isSuccess, onRemoveAsset]);

  const sortedDataForBulkDelete = useMemo(() => {
    return data.slice().sort((a, b) => {
      if (a.hasError === b.hasError) return 0;
      return a.hasError ? -1 : 1;
    });
  }, [data]);

  const instance = useTable(
    {
      columns,
      data: showBulkDeleteNotification ? sortedDataForBulkDelete : updatedData,
      initialState: {
        sortBy: [],
      } as any,
    },
    useSortBy,
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    instance as TableInstance<WellControlConfirmData> & {
      state: (typeof instance)['state'];
    };

  // Calculate failed wells count
  const failedWellsCount = sortedDataForBulkDelete.filter((well) => well.hasError).length;
  const totalWellsCount = sortedDataForBulkDelete.length;

  // Handle bulk removal of failed assets
  const handleBulkRemoveFailedAssets = () => {
    if (!onRemoveAsset) return;

    const failedAssets = sortedDataForBulkDelete.filter((well) => well.hasError);
    failedAssets.forEach((asset) => {
      onRemoveAsset(asset.assetId);
    });

    if (typeof onBulkRemoveFailedAssets === 'function') {
      onBulkRemoveFailedAssets();
    }
  };

  return (
    <article className='well-control-confirm-table'>
      {showBulkDeleteNotification && failedWellsCount > 0 && (
        <div className='bulk-delete-notification'>
          <div className='notification-content'>
            <div className='notification-message'>
              <p className='mwc-message-text'>
                Some wells are disabled or you don't have permission to {actionLabels.button.toLowerCase()}.
              </p>
              <p className='mwc-message-text'>
                Would you like to remove these wells from the {actionLabels.button.toLowerCase()} list?
              </p>
              <div className='mwc-failed-count'>
                {failedWellsCount} of {totalWellsCount} wells are not ready
              </div>
            </div>
            <div className='notification-actions'>
              <button
                className='bulk-remove-button'
                onClick={handleBulkRemoveFailedAssets}
                title='Remove all failed wells from the scan list'
              >
                <TrashIcon className='remove-icon' />
                Remove wells
              </button>
            </div>
          </div>
        </div>
      )}

      {showScanningProgressNotification && !(showBulkDeleteNotification && failedWellsCount > 0) && (
        <div className='scanning-notification'>
          <div className='notification-content'>
            <div className='notification-message'>
              {progressDisplay === 100 ? (
                failedCount > 0 ? (
                  <div className='success-notification-failed-assets'>
                    <span className='mwc-scan-message-text'>
                      {actionLabels.button} action complete. Some wells are disabled or you don’t have permission to{' '}
                      {actionLabels.button.toLowerCase()}.
                    </span>
                  </div>
                ) : (
                  <span className='mwc-scan-message-text'>
                    Great news, your wells {actionLabels.button.toLowerCase()} successfully. No issues detected.
                  </span>
                )
              ) : (
                <span className='mwc-scan-message-text'>
                  Hang tight, {actionLabels.progressive?.toLowerCase()}. This could take a little time depending on the
                  selection.
                  <br />
                  You can safely close this window; we’ll let you know when it finishes.
                </span>
              )}

              <span className='mwc-scan-count'>
                {`${successCount} of ${totalCount} wells | `}
                {failedCount > 0 && `${failedCount} failed | `}
                {progressDisplay === 100 ? (
                  <>
                    <CheckCircleIcon />
                    <span className='custom-view-complete-scan-text'>&nbsp;Complete </span>
                  </>
                ) : (
                  <>
                    <SignalIcon />
                    &nbsp;{actionLabels.progressive}...
                  </>
                )}
              </span>
            </div>
            <div className='progress-bar-container'>
              <div className='progress-bar'>
                <div className='progress-bar-fill uploading' style={{ width: `${progressDisplay}%` }}></div>
              </div>
              <span className='progress-percentage'>
                {Number.isInteger(progressDisplay) ? progressDisplay : progressDisplay.toFixed(2)}%
              </span>
            </div>
          </div>
        </div>
      )}
      <header className='header'>
        <div className='title-container'>
          <h2 className='title'>{actionLabels.button}</h2>
        </div>
        <Badge variant='default' size='md'>
          {data.length} wells
        </Badge>
      </header>
      <section
        className={`mwc-table-container ${isProcessing ? 'processing' : ''} ${showBulkDeleteNotification && failedWellsCount > 0 ? 'with-notification' : ''} ${showScanningProgressNotification ? 'with-scanning' : ''}`}
        {...getTableProps()}
      >
        {isProcessing && (
          <div className='processing-overlay'>
            <div className='processing-content'>
              <div className='spinner'></div>
              <span className='processing-text'>Processing...</span>
            </div>
          </div>
        )}
        {headerGroups.map((headerGroup) => (
          <div className='fixed-header' {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((col) => {
              const column = col as unknown as ColumnInstance<WellControlConfirmData> & {
                canSort: boolean;
                isSorted: boolean;
                isSortedDesc: boolean;
                getSortByToggleProps: () => any;
              };
              const columnClass =
                column.id === 'wellName'
                  ? 'mwc-well-name'
                  : column.id === 'lastGoodScan'
                    ? 'last-good-scan'
                    : column.id === 'commsStatus'
                      ? 'comms-status'
                      : column.id;
              return (
                <div className={`column ${columnClass}`}>
                  <div
                    className={`header-cell ${
                      column.id === 'select' ? 'center' : ''
                    } ${column.canSort ? 'sortable-header' : ''} ${column.isSorted ? 'sorted-header' : ''}`}
                    {...(column.canSort ? column.getSortByToggleProps() : {})}
                    title={column.canSort ? `Sort by ${column.render('Header')}` : undefined}
                  >
                    <div className='header-content'>
                      <div className='header-row'>
                        <span className='header-text'>{column.render('Header')}</span>
                        {column.canSort && column.isSorted && (
                          <span className={`sort-indicator ${column.isSortedDesc ? 'desc' : 'asc'}`}></span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div className='scrollable-body' {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <div className='data-row' {...row.getRowProps()}>
                {row.cells.map((cell) => {
                  const columnClass =
                    cell.column.id === 'wellName'
                      ? 'mwc-well-name'
                      : cell.column.id === 'lastGoodScan'
                        ? 'last-good-scan'
                        : cell.column.id === 'commsStatus'
                          ? 'comms-status'
                          : cell.column.id;
                  return (
                    <div className={`column ${columnClass}`} {...cell.getCellProps()}>
                      <div
                        className={`data-cell ${cell.column.id === 'wellName' ? 'mwc-well-name-cell' : ''} ${cell.column.id === 'select' ? 'center' : ''} ${cell.column.id === 'actions' ? 'custom-view-data-status' : ''} `}
                      >
                        {cell.render('Cell')}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </section>
    </article>
  );
};

export default WellControlConfirmTable;
